/* eslint-disable max-len */
import { Button } from '@mui/material';
import { ReactNode, memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { OrderStatuses } from 'entities/Order/model/consts/orderStatuses';
import {
    getIsOrderSubmissionLocked,
    getIsRechecking,
    getIsSubmitting,
} from 'entities/Order/model/selectors/ordersSelectors';
import { fetchOrder } from 'entities/Order/model/services/fetchOrder/fetchOrder';
import { orderActions } from 'entities/Order/model/slices/OrderSlice';
import { hasOrderRecheckChanges } from 'features/changeOrderStatus/lib/hasOrderRecheckChanges';
import { changeOrderStatusService } from 'features/changeOrderStatus/model/services/changeOrderStatusService';
import { runOrderRecheck } from 'features/changeOrderStatus/model/services/orderRecheckService';
import { OrderRecheckResponse } from 'features/changeOrderStatus/model/types/orderRecheckSchema';
import { PriceAvailabilityCheckModal } from 'features/changeOrderStatus/ui/PriceAvailabilityCheckModal/PriceAvailabilityCheckModal';
import { isAutomationSkipChecksUserAgent } from 'shared/const/automation';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import BlockPurchaseOrderModal from 'shared/ui/BlockPurchaseOrderModal/BlockPurchaseOrderModal';
import BlockRejectOrderModal from 'shared/ui/BlockRejectOrderModal/BlockRejectOrderModal';
import { Loader } from 'shared/ui/Loader/Loader';

interface ChangeOrderStatusButtonProps {
    children: ReactNode;
    newStatus: OrderStatuses;
    messageOnSuccess: string;
    orderId: string;
    variant?: 'text' | 'contained' | 'outlined';
    disabled?: boolean;
    isRequiredPoNumberForOrder?: boolean;
}

export const ChangeOrderStatusButton = memo(
    ({
        newStatus,
        children,
        variant,
        orderId,
        messageOnSuccess,
        disabled,
        isRequiredPoNumberForOrder,
    }: ChangeOrderStatusButtonProps) => {
        const dispatch = useAppDispatch();

        const isSubmitting = useSelector(getIsSubmitting);
        const isRechecking = useSelector(getIsRechecking);
        const isOrderSubmissionLocked = useSelector(getIsOrderSubmissionLocked);
        const isButtonDisabled = disabled || isOrderSubmissionLocked;

        const [showBlockPurchaseModal, setShowBlockPurchaseModal] =
            useState(false);
        const [showBlockRejectModal, setShowBlockRejectModal] = useState(false);
        const [showPriceAvailabilityModal, setShowPriceAvailabilityModal] =
            useState(false);
        const [recheckData, setRecheckData] =
            useState<OrderRecheckResponse | null>(null);

        const onCloseBlockPurchaseModal = () =>
            setShowBlockPurchaseModal(false);

        const onCloseBlockRejectModal = () => setShowBlockRejectModal(false);

        const onClosePriceAvailabilityModal = useCallback(() => {
            setShowPriceAvailabilityModal(false);
            setRecheckData(null);
            dispatch(orderActions.setIsPriceAvailabilityModalOpen(false));
        }, [dispatch]);

        const submitChangeOrderStatus = useCallback(async () => {
            dispatch(orderActions.setIsSubmitting(true));

            const response = await dispatch(
                changeOrderStatusService({
                    orderId,
                    status: newStatus,
                }),
            );

            if (response.meta.requestStatus !== 'rejected') {
                await dispatch(fetchOrder({ id: orderId }));
                toast(messageOnSuccess);
            } else {
                toast(response?.payload as string);
            }
            onCloseBlockPurchaseModal();
            onCloseBlockRejectModal();
            onClosePriceAvailabilityModal();
            dispatch(orderActions.resetSubmissionState());
        }, [
            dispatch,
            messageOnSuccess,
            newStatus,
            orderId,
            onClosePriceAvailabilityModal,
        ]);

        const checkPriceAndAvailability = useCallback(async () => {
            dispatch(orderActions.setIsRechecking(true));
            try {
                const response = await dispatch(runOrderRecheck({ orderId }));

                if (!runOrderRecheck.fulfilled.match(response)) {
                    // If recheck fails, allow submission with no error toast
                    return true;
                }

                const data = response.payload;
                if (hasOrderRecheckChanges(data)) {
                    setRecheckData(data);
                    setShowPriceAvailabilityModal(true);
                    dispatch(
                        orderActions.setIsPriceAvailabilityModalOpen(true),
                    );
                    return false;
                }

                return true;
            } catch {
                // If recheck fails, allow submission with no error toast
                return true;
            } finally {
                dispatch(orderActions.setIsRechecking(false));
            }
        }, [dispatch, orderId]);

        const handleReviewOrder = useCallback(async () => {
            onClosePriceAvailabilityModal();
        }, [onClosePriceAvailabilityModal]);

        const handleSubmitFromModal = useCallback(() => {
            onClosePriceAvailabilityModal();
            submitChangeOrderStatus();
        }, [onClosePriceAvailabilityModal, submitChangeOrderStatus]);

        // Automatically refresh order data when modal opens with recheck changes
        useEffect(() => {
            if (showPriceAvailabilityModal && recheckData) {
                dispatch(fetchOrder({ id: orderId }));
            }
        }, [showPriceAvailabilityModal, recheckData, dispatch, orderId]);

        const onChangeOrderStatus = useCallback(async () => {
            if (isSubmitting || isRechecking) return;
            if (isRequiredPoNumberForOrder) {
                setShowBlockPurchaseModal(true);
                return;
            }
            if (disabled) return;
            if (newStatus === OrderStatuses.REJECTED) {
                setShowBlockRejectModal(true);
                return;
            }

            // For APPROVED status, run price/availability check first
            if (
                newStatus === OrderStatuses.APPROVED &&
                !isAutomationSkipChecksUserAgent()
            ) {
                const canProceed = await checkPriceAndAvailability();
                if (!canProceed) {
                    // Modal is shown, don't proceed with submission
                    return;
                }
            }

            submitChangeOrderStatus();
        }, [
            disabled,
            isSubmitting,
            isRechecking,
            isRequiredPoNumberForOrder,
            newStatus,
            checkPriceAndAvailability,
            submitChangeOrderStatus,
        ]);

        return (
            <>
                <Button
                    startIcon={
                        (isSubmitting || isRechecking) && (
                            <Loader color="inherit" />
                        )
                    }
                    className={isButtonDisabled ? 'Mui-disabled' : ''}
                    disabled={isButtonDisabled}
                    style={{
                        pointerEvents: isButtonDisabled ? 'none' : 'auto',
                    }}
                    variant={variant || 'outlined'}
                    onClick={onChangeOrderStatus}
                >
                    {children}
                </Button>
                <BlockPurchaseOrderModal
                    showBlockModal={showBlockPurchaseModal}
                    onCloseBlockModal={onCloseBlockPurchaseModal}
                />
                <BlockRejectOrderModal
                    showBlockModal={showBlockRejectModal}
                    onCloseBlockModal={onCloseBlockRejectModal}
                    onSubmitModal={submitChangeOrderStatus}
                    isLoading={isSubmitting}
                />
                {recheckData && (
                    <PriceAvailabilityCheckModal
                        open={showPriceAvailabilityModal}
                        onClose={onClosePriceAvailabilityModal}
                        onSubmit={handleSubmitFromModal}
                        onReview={handleReviewOrder}
                        recheckData={recheckData}
                        isLoading={isSubmitting}
                    />
                )}
            </>
        );
    },
);
